class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.bindFrameScrollLock();
    this.observePromoBar();
  }

  // The promo bar is re-rendered by several code paths (add to cart, quantity changes, section
  // refreshes), so watch the DOM and celebrate every time the quantity discount tier goes up
  // (2 pcs -10%, 3 pcs -15%, 4 pcs -20%).
  observePromoBar() {
    this._promoTier = this.promoTier();
    this._promoObserver = new MutationObserver(() => {
      const tier = this.promoTier();
      if (tier > this._promoTier) this.celebratePromo();
      this._promoTier = tier;
    });
    this._promoObserver.observe(this, { childList: true, subtree: true });
  }

  promoTier() {
    const bar = this.querySelector('.cart-drawer__announcement');
    return bar ? Number(bar.dataset.promoTier) || 0 : 0;
  }

  celebratePromo() {
    const bar = this.querySelector('.cart-drawer__announcement');
    if (!bar) return;

    bar.classList.remove('is-celebrating');
    void bar.offsetWidth;
    bar.classList.add('is-celebrating');

    clearTimeout(this._promoTimer);
    this._promoTimer = setTimeout(() => bar.classList.remove('is-celebrating'), 3600);
  }

  frameScrollShells() {
    return [this, this.querySelector('#CartDrawer'), this.querySelector('.drawer__inner')].filter(Boolean);
  }

  lockFrameScroll() {
    this.frameScrollShells().forEach((el) => {
      if (el.scrollTop) el.scrollTop = 0;
    });
  }

  bindFrameScrollLock() {
    this.frameScrollShells().forEach((el) => {
      if (el._cartFrameScrollLock) return;
      el._cartFrameScrollLock = () => this.lockFrameScroll();
      el.addEventListener('scroll', el._cartFrameScrollLock, { passive: true });
    });
    if (!this._frameFocusLock) {
      this._frameFocusLock = () => this.lockFrameScroll();
      this.addEventListener('focusin', this._frameFocusLock);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.lockFrameScroll();
      if (typeof window.initCartDrawerUpsell === 'function') {
        window.initCartDrawerUpsell(this, { resetClosed: true });
      }
      if (typeof window.initCartGiftBoxesJump === 'function') {
        window.initCartGiftBoxesJump();
      }
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  initDrawerWidgets() {
    this.bindFrameScrollLock();
    this.lockFrameScroll();
    if (typeof window.initCartDrawerUpsell === 'function') {
      window.initCartDrawerUpsell();
    }
    if (typeof window.initCartPromoBanner === 'function') {
      window.initCartPromoBanner();
    }
    if (typeof window.initCartGiftBoxesJump === 'function') {
      window.initCartGiftBoxesJump();
    }
  }

  // Swap only the blocks that change. Replacing #CartDrawer or .drawer__inner
  // disconnects the drawer and drops nested sections like gift boxes / upsell.
  patchContents(parsedState, options = {}) {
    const sectionHTML = parsedState?.sections?.['cart-drawer'];
    if (!sectionHTML || this.classList.contains('is-empty')) return false;

    const nextDrawer = new DOMParser().parseFromString(sectionHTML, 'text/html').querySelector('cart-drawer');
    if (!nextDrawer || nextDrawer.classList.contains('is-empty')) return false;

    const pairs = [];
    for (const selector of [
      '.cart-drawer__announcement',
      '.cart-promo-banner',
      '.cart-drawer__form',
      '[data-cart-gift-boxes]',
      '[data-cart-drawer-upsell]',
      '.cart-drawer__footer',
    ]) {
      const target = this.querySelector(selector);
      const next = nextDrawer.querySelector(selector);
      if (!target && next) return false;
      if (target) pairs.push([target, next]);
    }

    const scroller = this.querySelector('cart-drawer-items');
    const scrollTop = options.preserveScroll !== false && scroller ? scroller.scrollTop : null;

    for (const [target, next] of pairs) {
      if (next) {
        target.replaceWith(next);
      } else {
        target.remove();
      }
    }

    const bubble = document.getElementById('cart-icon-bubble');
    const bubbleHTML = parsedState.sections['cart-icon-bubble'];
    if (bubble && bubbleHTML) {
      bubble.innerHTML = this.getSectionInnerHTML(bubbleHTML, '.shopify-section');
    }

    this.classList.toggle('is-empty', Number(parsedState.item_count) === 0);
    this.initDrawerWidgets();
    this.lockFrameScroll();
    if (scrollTop != null) this.restoreItemsScroll(scrollTop);
    return true;
  }

  restoreItemsScroll(scrollTop) {
    this._savedItemsScroll = scrollTop;
    const apply = () => {
      this.lockFrameScroll();
      const scroller = this.querySelector('cart-drawer-items');
      if (scroller && this._savedItemsScroll != null) scroller.scrollTop = this._savedItemsScroll;
    };

    apply();
    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });
    clearTimeout(this._savedItemsScrollTimer);
    this._savedItemsScrollTimer = setTimeout(() => {
      apply();
      this._savedItemsScroll = null;
    }, 250);
  }

  renderContents(parsedState, options = {}) {
    const preserveScroll = Boolean(options.preserveScroll);
    const alreadyOpen = this.classList.contains('active');

    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;

    if (alreadyOpen && this.patchContents(parsedState, { preserveScroll: true })) {
      return;
    }

    const scroller = this.querySelector('cart-drawer-items');
    const savedScroll = preserveScroll && scroller ? scroller.scrollTop : null;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      if (!alreadyOpen) {
        this.open();
      }
      this.initDrawerWidgets();
      if (savedScroll != null) this.restoreItemsScroll(savedScroll);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
